import { FormbrewError } from "./errors.ts";
import {
  collectFormbrewSubmission,
  resolveFormbrewMetadata,
  synchronizeConfirmFields,
} from "./form.ts";
import { isPublicFormDefinition } from "./guards.ts";
import {
  createRenderedForm,
  createStatusElement,
  resolveRendererTarget,
  synchronizeChoiceGroups,
} from "./renderer.dom.ts";
import {
  createRendererLifecycle,
  isAbortError,
  normalizeRendererError,
} from "./renderer.lifecycle.ts";
import type { FormbrewFormController, MountFormbrewFormOptions } from "./renderer.types.ts";
import type {
  FormbrewSubmissionMetadata,
  FormbrewSubmissionResult,
  PublicFormDefinition,
} from "./types.ts";

type SubmitOptions = {
  metadata?: FormbrewSubmissionMetadata;
  signal: AbortSignal;
};

type Cleanup = () => void;

let mountedFormCount = 0;

/** Fetches and mounts an accessible Formbrew form into a target element. */
export const mountFormbrewForm = async (
  target: string | HTMLElement,
  options: MountFormbrewFormOptions,
): Promise<FormbrewFormController> => {
  const targetElement = resolveRendererTarget(target);
  const lifecycle = createRendererLifecycle(options.signal);
  let destroyed = false;
  let removeExternalAbortListener: Cleanup = () => {};

  targetElement.setAttribute("aria-busy", "true");
  targetElement.replaceChildren(
    createStatusElement("Loading form...", "status", "fb-formbrew-status"),
  );

  const definition = await loadDefinition(
    targetElement,
    options,
    lifecycle.signal,
    lifecycle.cleanup,
  );

  if (lifecycle.signal.aborted) {
    lifecycle.cleanup();
    clearRendererTarget(targetElement);

    throw lifecycle.signal.reason ?? new DOMException("Aborted", "AbortError");
  }

  mountedFormCount += 1;

  const instanceId = `fb-${mountedFormCount}`;
  const form = createRenderedForm(definition, instanceId);
  const submitButton = form.querySelector<HTMLButtonElement>(".fb-submit");
  const status = form.querySelector<HTMLElement>(".fb-status");

  if (!submitButton || !status) {
    lifecycle.cleanup();
    throw new FormbrewError("Unable to render the Formbrew form.", { code: "RendererError" });
  }

  let submitting = false;
  let controller: FormbrewFormController;

  const synchronizeCrossFieldValidity = () => {
    synchronizeChoiceGroups(form, definition.fields);
    synchronizeConfirmFields(form, definition.fields);
  };

  const reset = () => {
    form.reset();
    status.textContent = "";
    status.className = "fb-status";
    synchronizeCrossFieldValidity();
  };

  const handleChange = () => {
    synchronizeCrossFieldValidity();
  };

  const handleReset = () => {
    // Reset events fire before the browser restores the controls' default values.
    queueMicrotask(() => {
      if (!destroyed) {
        synchronizeCrossFieldValidity();
      }
    });
  };

  const destroy = () => {
    if (destroyed) {
      return;
    }

    destroyed = true;
    lifecycle.abort();
    lifecycle.cleanup();
    removeExternalAbortListener();
    form.removeEventListener("change", handleChange);
    form.removeEventListener("input", handleChange);
    form.removeEventListener("reset", handleReset);
    form.removeEventListener("submit", handleSubmit);

    if (targetElement.contains(form)) {
      targetElement.replaceChildren();
    }

    targetElement.removeAttribute("aria-busy");
  };

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();

    if (submitting || destroyed) {
      return;
    }

    synchronizeCrossFieldValidity();
    if (!form.reportValidity()) {
      return;
    }

    submitting = true;
    submitButton.disabled = true;
    form.setAttribute("aria-busy", "true");
    status.textContent = "Submitting...";
    status.className = "fb-status";

    let result: FormbrewSubmissionResult;

    try {
      const submission = collectFormbrewSubmission(form);
      const configuredMetadata = resolveFormbrewMetadata(options.metadata);
      const metadata = { ...configuredMetadata, ...submission.metadata };
      const submitOptions = createSubmitOptions(metadata, lifecycle.signal);

      result = await options.client.submit(submission.values, submitOptions);

      if (destroyed) {
        return;
      }
    } catch (error) {
      if (destroyed || isAbortError(error)) {
        return;
      }

      const formbrewError = normalizeRendererError(error, "Submission failed.");
      status.textContent = formbrewError.message;
      status.className = "fb-status fb-status--error";
      options.onError?.(formbrewError, controller);

      return;
    } finally {
      submitting = false;
      submitButton.disabled = false;
      form.removeAttribute("aria-busy");
    }

    form.reset();
    synchronizeCrossFieldValidity();
    status.textContent = result.message;
    status.className = "fb-status fb-status--success";
    options.onSuccess?.(result, controller);
  };

  controller = {
    definition,
    destroy,
    form,
    reset,
    target: targetElement,
  };

  if (options.signal) {
    const destroyFromExternalAbort = () => {
      destroy();
    };

    options.signal.addEventListener("abort", destroyFromExternalAbort, { once: true });
    removeExternalAbortListener = () => {
      options.signal?.removeEventListener("abort", destroyFromExternalAbort);
    };
  }

  form.addEventListener("change", handleChange);
  form.addEventListener("input", handleChange);
  form.addEventListener("reset", handleReset);
  form.addEventListener("submit", handleSubmit);
  synchronizeCrossFieldValidity();
  targetElement.replaceChildren(form);
  targetElement.removeAttribute("aria-busy");

  return controller;
};

const createSubmitOptions = (
  metadata: FormbrewSubmissionMetadata,
  signal: AbortSignal,
): SubmitOptions => {
  const submitOptions: SubmitOptions = { signal };

  if (Object.keys(metadata).length > 0) {
    submitOptions.metadata = metadata;
  }

  return submitOptions;
};

const loadDefinition = async (
  target: HTMLElement,
  options: MountFormbrewFormOptions,
  signal: AbortSignal,
  cleanup: Cleanup,
): Promise<PublicFormDefinition> => {
  try {
    if (options.definition) {
      if (!isPublicFormDefinition(options.definition)) {
        throw new FormbrewError("The form definition is invalid.", { code: "ConfigurationError" });
      }

      return options.definition;
    }

    return await options.client.fetchForm({ signal });
  } catch (error) {
    cleanup();
    target.removeAttribute("aria-busy");

    if (signal.aborted || isAbortError(error)) {
      target.replaceChildren();

      throw signal.reason ?? error;
    }

    const formbrewError = normalizeRendererError(error, "Unable to load this form.");
    target.replaceChildren(
      createStatusElement(formbrewError.message, "alert", "fb-formbrew-error"),
    );

    throw formbrewError;
  }
};

const clearRendererTarget = (target: HTMLElement): void => {
  target.removeAttribute("aria-busy");
  target.replaceChildren();
};
